import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

const AnimatedLogo = ({ size = 'default', className = '' }) => {
  const { isFeatureEnabled } = useFeatureFlags();
  const [isHovered, setIsHovered] = useState(false);
  
  if (!isFeatureEnabled('animatedLogo')) {
    return (
      <div className={`flex items-center space-x-3 ${className}`}>
        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
          <div className="w-4 h-4 bg-accent rounded-sm"></div>
        </div>
        <div className="flex flex-col">
          <span className="text-xl font-bold text-primary tracking-tight michroma">SIGNA</span>
          <span className="text-xs text-text-secondary font-medium -mt-1">Technologies</span>
        </div>
      </div>
    );
  }

  const sizeStyles = {
    small: {
      container: 'w-6 h-6',
      inner: 'w-3 h-3',
      text: 'text-lg',
      subtext: 'text-xs'
    },
    default: {
      container: 'w-8 h-8',
      inner: 'w-4 h-4',
      text: 'text-xl',
      subtext: 'text-xs'
    },
    large: {
      container: 'w-12 h-12',
      inner: 'w-6 h-6',
      text: 'text-2xl',
      subtext: 'text-sm'
    }
  };

  const styles = sizeStyles[size] || sizeStyles.default;

  return (
    <motion.div 
      className={`flex items-center space-x-3 cursor-pointer ${className}`}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <div className="relative">
        {/* Main logo container */}
        <motion.div 
          className={`${styles.container} bg-primary rounded-lg flex items-center justify-center relative overflow-hidden`}
          animate={{ 
            scale: isHovered ? 1.1 : 1,
            rotate: isHovered ? 5 : 0
          }}
          transition={{ 
            duration: 0.3,
            type: "spring",
            stiffness: 300,
            damping: 20
          }}
        >
          {/* Central logo element - focus on middle part */}
          <motion.div 
            className={`${styles.inner} bg-accent rounded-sm relative z-10`}
            animate={{ 
              scale: isHovered ? 1.2 : 1,
              rotateY: isHovered ? 180 : 0
            }}
            transition={{ 
              duration: 0.4,
              type: "spring",
              stiffness: 400,
              damping: 25
            }}
          >
            {/* Inner glow effect on hover */}
            <motion.div
              className="absolute inset-0 bg-white/30 rounded-sm"
              animate={{ 
                opacity: isHovered ? 0.8 : 0.2,
                scale: isHovered ? 1.3 : 1
              }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
          
          {/* Subtle corner indicators */}
          <div className="absolute top-0.5 left-0.5 w-1 h-1 bg-accent/40 rounded-full" />
          <div className="absolute top-0.5 right-0.5 w-1 h-1 bg-accent/40 rounded-full" />
          <div className="absolute bottom-0.5 left-0.5 w-1 h-1 bg-accent/40 rounded-full" />
          <div className="absolute bottom-0.5 right-0.5 w-1 h-1 bg-accent/40 rounded-full" />
        </motion.div>
      </div>
      
      {/* Text with SIGNA name reveal animation */}
      <div className="flex flex-col overflow-hidden">
        <motion.div
          className="relative"
          animate={{ 
            width: isHovered ? "auto" : "auto",
            opacity: isHovered ? 1 : 1
          }}
          transition={{ duration: 0.4 }}
        >
          <motion.span 
            className={`${styles.text} font-bold text-primary tracking-tight michroma block`}
            animate={{ 
              letterSpacing: isHovered ? "0.2em" : "0.05em",
              color: isHovered ? "var(--color-accent)" : "var(--color-primary)"
            }}
            transition={{ duration: 0.3 }}
          >
            SIGNA
          </motion.span>
          
          {/* Animated underline on hover */}
          <motion.div
            className="absolute bottom-0 left-0 h-0.5 bg-accent origin-left"
            animate={{ 
              scaleX: isHovered ? 1 : 0,
              opacity: isHovered ? 1 : 0
            }}
            transition={{ duration: 0.3 }}
            style={{ width: "100%" }}
          />
        </motion.div>
        
        <motion.span 
          className={`${styles.subtext} text-text-secondary font-medium -mt-1`}
          animate={{ 
            opacity: isHovered ? 0.8 : 0.7,
            x: isHovered ? 2 : 0
          }}
          transition={{ duration: 0.3 }}
        >
          Technologies
        </motion.span>
      </div>
    </motion.div>
  );
};

export default AnimatedLogo;